// Scanner helpers for aliasScope.ts — keyword classification, state-machine
// transitions, and the paren-skip utility. Split out to keep the public
// aliasScope.ts under the project's 200-line budget.
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Scope Resolver — aliases.

import type { AliasSource } from '../types';
import { trySkipNonCode, type SkipContext } from '../classify/tokenSkip';

export type OuterKind = 'select' | 'update' | 'delete' | 'insert' | 'merge' | 'with' | 'unknown';

// Keywords that close a FROM/JOIN list and forbid further alias collection
// until a new FROM/JOIN/APPLY/LATERAL/INTO/USING opens one.
export const CLAUSE_TERMINATORS: ReadonlySet<string> = new Set([
  'WHERE', 'GROUP', 'ORDER', 'HAVING', 'LIMIT', 'OFFSET', 'FETCH',
  'UNION', 'INTERSECT', 'EXCEPT', 'RETURNING', 'SET', 'VALUES',
  'ON', 'WHEN', 'WINDOW', 'RECURSIVE',
]);

// Keywords that drive the alias-scope state machine. Any identifier NOT in
// this set (or a quoted identifier) is a candidate target/alias.
export function isStructuralKeyword(upper: string): boolean {
  return (
    upper === 'SELECT' || upper === 'WITH' || upper === 'INSERT' || upper === 'UPDATE'
    || upper === 'DELETE' || upper === 'MERGE'
    || upper === 'FROM' || upper === 'JOIN' || upper === 'ON' || upper === 'USING'
    || upper === 'INTO' || upper === 'LATERAL' || upper === 'APPLY' || upper === 'AS'
    || upper === 'INNER' || upper === 'LEFT' || upper === 'RIGHT' || upper === 'FULL'
    || upper === 'OUTER' || upper === 'CROSS' || upper === 'NATURAL' || upper === 'STRAIGHT_JOIN'
    || CLAUSE_TERMINATORS.has(upper)
  );
}

export interface ScannerState {
  expect: 'target' | 'alias' | 'none';
  pendingSource: AliasSource;
  outerKind: OuterKind;
  prevKw: string | null;
}

// Process a structural keyword: mutate state, establishing alias-collection
// expectations for the following identifier(s) / paren-body.
// eslint-disable-next-line complexity
export function applyKeyword(state: ScannerState, kw: string): void {
  if (state.outerKind === 'unknown') {
    if (kw === 'SELECT') state.outerKind = 'select';
    else if (kw === 'WITH') state.outerKind = 'with';
    else if (kw === 'INSERT') state.outerKind = 'insert';
    else if (kw === 'DELETE') state.outerKind = 'delete';
    else if (kw === 'MERGE') state.outerKind = 'merge';
    else if (kw === 'UPDATE') {
      state.outerKind = 'update';
      state.expect = 'target'; state.pendingSource = 'from'; state.prevKw = kw; return;
    }
  }

  if (kw === 'APPLY' && state.prevKw === 'CROSS') {
    state.expect = 'target'; state.pendingSource = 'cross-apply'; state.prevKw = kw; return;
  }
  if (kw === 'APPLY' && state.prevKw === 'OUTER') {
    state.expect = 'target'; state.pendingSource = 'outer-apply'; state.prevKw = kw; return;
  }

  if (kw === 'FROM') { state.expect = 'target'; state.pendingSource = 'from'; state.prevKw = kw; return; }
  if (kw === 'JOIN' || kw === 'STRAIGHT_JOIN') {
    state.expect = 'target'; state.pendingSource = 'join'; state.prevKw = 'JOIN'; return;
  }
  if (kw === 'LATERAL') { state.expect = 'target'; state.pendingSource = 'lateral'; state.prevKw = kw; return; }
  if (kw === 'USING' && state.outerKind === 'merge') {
    state.expect = 'target'; state.pendingSource = 'using'; state.prevKw = kw; return;
  }
  if (kw === 'INTO' && (state.outerKind === 'merge' || state.outerKind === 'insert')) {
    state.expect = 'target'; state.pendingSource = 'from'; state.prevKw = kw; return;
  }
  if (CLAUSE_TERMINATORS.has(kw)) {
    state.expect = 'none'; state.prevKw = kw; return;
  }

  // AS skips through — the next identifier is the alias.
  state.prevKw = kw;
}

// Advance past a balanced `(...)` region starting at the `(` index. Returns
// the index just past the matching `)`, or endExclusive if unbalanced.
export function skipParen(
  source: string,
  openIdx: number,
  endExclusive: number,
  ctx: SkipContext,
): number {
  let j = openIdx + 1;
  let d = 1;
  while (j < endExclusive && d > 0) {
    const skipped = trySkipNonCode(source, j, endExclusive, ctx);
    if (skipped >= 0) { j = skipped; continue; }
    const ch = source[j];
    if (ch === '(') { d++; j++; continue; }
    if (ch === ')') { d--; j++; if (d === 0) return j; continue; }
    j++;
  }
  return j;
}
